<template>
  <div class="w-full">
    <div class="p-6 space-y-6">
      <!-- Выбор типа уведомления -->
      <div>
        <h4 class="text-lg font-medium text-hui-foreground mb-3">Тип уведомления</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div
            v-for="type in notificationTypes"
            :key="type.value"
            class="p-4 border rounded-lg cursor-pointer transition-colors duration-200"
            :class="selectedType === type.value
              ? 'border-hui-primary bg-hui-primary/10'
              : 'border-hui-divider hover:bg-hui-default-100'"
            @click="selectNotificationType(type.value)"
          >
            <div class="flex items-start gap-3">
              <UIcon :name="type.icon" class="w-6 h-6 text-hui-primary mt-1" />
              <div class="flex-1">
                <div class="font-medium text-hui-foreground">{{ type.label }}</div>
                <div class="text-sm text-hui-foreground/60">{{ type.description }}</div>
                <div v-if="audienceStats" class="text-xs text-hui-foreground/40 mt-1">
                  Аудитория: {{ getAudienceCount(type.value) }} пользователей
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Дополнительные параметры для типа "по полу" -->
      <div v-if="selectedType === 'gender'">
        <h5 class="text-sm font-medium text-hui-foreground mb-2">Выберите пол</h5>
        <div class="flex gap-4">
          <label class="flex items-center gap-2">
            <input
              v-model="genderFilter"
              type="radio"
              value="male"
              class="w-4 h-4"
            />
            <span class="text-hui-foreground">Мужчины ({{ audienceStats?.male_users || 0 }})</span>
          </label>
          <label class="flex items-center gap-2">
            <input
              v-model="genderFilter"
              type="radio"
              value="female"
              class="w-4 h-4"
            />
            <span class="text-hui-foreground">Женщины ({{ audienceStats?.female_users || 0 }})</span>
          </label>
        </div>
      </div>

      <!-- Дополнительные параметры для индивидуального уведомления -->
      <div v-if="selectedType === 'individual'">
        <UiKitInput
          v-model="individualUserIdStr"
          label="ID пользователя"
          type="number"
          placeholder="Введите ID пользователя"
        />
      </div>

      <!-- Шаблоны -->
      <div>
        <h4 class="text-lg font-medium text-hui-foreground mb-3">Шаблоны</h4>
        <div class="space-y-3">
          <div
            v-for="template in currentTemplates"
            :key="template.id"
            class="p-3 border rounded-lg cursor-pointer transition-colors duration-200"
            :class="selectedTemplate === template.id
              ? 'border-hui-primary bg-hui-primary/10'
              : 'border-hui-divider hover:bg-hui-default-100'"
            @click="selectTemplate(template)"
          >
            <div class="flex items-start gap-3">
              <UIcon name="i-heroicons-heart" class="w-5 h-5 text-hui-success mt-1" />
              <div class="flex-1">
                <div class="font-medium text-hui-foreground">{{ template.title }}</div>
                <div class="text-sm text-hui-foreground/60">{{ template.body }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Кнопка "Без шаблона" -->
      <div>
        <UiKitButton color="default" variant="flat" class="w-full justify-center" @click="showCustomForm = !showCustomForm">
          {{ showCustomForm ? 'Скрыть форму' : 'Без шаблона' }}
        </UiKitButton>
      </div>

      <!-- Кастомная форма -->
      <div v-if="showCustomForm" class="space-y-4">
        <!-- Заголовок -->
        <div>
          <label class="block text-sm font-medium text-hui-foreground/80 mb-1">
            Заголовок *
            <span class="text-xs text-hui-foreground/40 ml-2">{{ customForm.title.length }}/100</span>
          </label>
          <UiKitInput
            v-model="customForm.title"
            label=""
            placeholder="Введите заголовок"
            :maxlength="100"
          />
        </div>

        <!-- Текст -->
        <div>
          <label class="block text-sm font-medium text-hui-foreground/80 mb-1">
            Текст сообщения *
            <span class="text-xs text-hui-foreground/40 ml-2">{{ customForm.body.length }}/300</span>
          </label>
          <UiKitTextarea
            v-model="customForm.body"
            label=""
            placeholder="Введите текст сообщения"
            :maxlength="300"
            :rows="4"
          />
        </div>

        <!-- Дополнительные данные -->
        <div>
          <UiKitTextarea
            v-model="customForm.dataJson"
            label="Дополнительные данные (JSON)"
            placeholder='{"key": "value"}'
            :rows="3"
          />
          <div v-if="dataJsonError" class="text-hui-danger text-xs mt-1">
            {{ dataJsonError }}
          </div>
        </div>

        <!-- Переменные -->
        <div class="p-3 bg-hui-default-100 rounded-lg text-xs">
          <p class="text-hui-foreground/80 mb-2">
            Вы можете использовать переменные в тексте сообщения. Для этого введите &#123;&#123; variable &#125;&#125; в тексте сообщения.
          </p>
          <div class="text-hui-foreground">
            <strong>Доступные переменные:</strong>
            <ul class="list-disc list-inside mt-1 space-y-1">
              <li><code>username</code> - имя пользователя</li>
              <li><code>birth_date</code> - день рождения пользователя</li>
              <li><code>balance</code> - количество баллов пользователя</li>
              <li><code>favorite_dish</code> - любимое блюдо пользователя</li>
            </ul>
          </div>
        </div>

        <!-- Планирование -->
        <!-- Служебное объявление vs реклама. Без этого разделения любая рассылка
             считалась рекламной и резалась по согласиям — предупредить об аварии
             было бы нельзя, дошло бы только до давших согласие на акции. -->
        <div v-if="selectedType !== 'individual'">
          <h5 class="text-sm font-medium text-hui-foreground mb-2">Тип сообщения</h5>
          <div class="flex items-start gap-2">
            <input
              v-model="isServiceMessage"
              type="checkbox"
              class="w-4 h-4 mt-0.5 shrink-0"
            />
            <div>
              <span class="text-sm text-hui-foreground">Служебное объявление</span>
              <p class="text-xs text-hui-foreground/60 mt-0.5">
                {{ isServiceMessage
                  ? 'Дойдёт всем в сегменте: смена режима работы, авария, важное объявление. Рекламой не является.'
                  : 'Рекламная рассылка: дойдёт только тем, кто дал согласие на акции (ст. 18 ФЗ «О рекламе»).' }}
              </p>
              <p v-if="isServiceMessage" class="text-xs text-hui-warning mt-1">
                Не отмечайте акции и промокоды служебными — это реклама, и на неё
                нужно согласие адресата.
              </p>
            </div>
          </div>
        </div>

        <div>
          <h5 class="text-sm font-medium text-hui-foreground mb-2">Планирование отправки</h5>
          <div class="flex items-center gap-2 mb-3 max-sm:flex-col">
            <input
              v-model="isScheduled"
              type="checkbox"
              class="w-4 h-4"
            />
            <span class="text-sm text-hui-foreground">Запланировать отправку</span>
          </div>

          <div v-if="isScheduled" class="flex gap-4">
            <div class="flex-1">
              <label class="block text-xs text-hui-foreground/60 mb-1">Дата</label>
              <UiKitInput
                v-model="customForm.scheduleDate"
                label=""
                type="date"
                :min="getTodayDate()"
              />
            </div>
            <div class="flex-1">
              <label class="block text-xs text-hui-foreground/60 mb-1">Время</label>
              <UiKitInput
                v-model="customForm.scheduleTime"
                label=""
                type="time"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Кнопки действий -->
      <div class="space-y-3">
        <UiKitButton
          color="primary"
          class="w-full justify-center"
          :loading="loading"
          :disabled="!canSend"
          @click="sendPush"
        >
          <template #leading><Icon name="heroicons:paper-airplane" class="w-4 h-4" /></template>
          {{ isScheduled ? 'Запланировать отправку' : 'Отправить сейчас' }}
        </UiKitButton>

        <UiKitButton
          v-if="selectedType !== 'individual'"
          color="default"
          variant="flat"
          class="w-full justify-center"
          :loading="testLoading"
          :disabled="!canSendTest"
          @click="sendTestPush"
        >
          <template #leading><Icon name="heroicons:beaker" class="w-4 h-4" /></template>
          Отправить тестовое уведомление
        </UiKitButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import consola from 'consola'
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useModalStore } from '@shared/model'
import APIAdminNotification from '~/fsd-pages/admin/api'
import type {
  NotificationType,
  IndividualNotifyPayload,
  GenderNotifyPayload,
  AbandonedCartNotifyPayload,
  NewGuestsNotifyPayload,
  NotificationTemplate,
} from '@entities/notification'

const modalStore = useModalStore()

// Состояние
const loading = ref(false)
const testLoading = ref(false)
const showCustomForm = ref(false)
const selectedType = ref<NotificationType>('individual')
const selectedTemplate = ref('')
const genderFilter = ref<'male' | 'female'>('male')
const individualUserIdStr = ref('')
const individualUserId = computed(() => individualUserIdStr.value ? Number(individualUserIdStr.value) : undefined)
const isScheduled = ref(false)
// Служебное объявление уходит всему сегменту; рекламное режется по согласиям.
// По умолчанию false: реклама — более частый и более опасный случай, и лучше
// недоставить её несогласившимся, чем разослать рекламу под видом служебной.
const isServiceMessage = ref(false)

// Статистика аудитории
const audienceStats = ref<{
  total_users: number
  male_users: number
  female_users: number
  users_with_abandoned_carts: number
  new_guests: number
} | null>(null)

// Типы уведомлений
const notificationTypes = [
  {
    value: 'individual' as const,
    label: 'Индивидуальное',
    description: 'Отправить уведомление конкретному пользователю',
    icon: 'i-heroicons-user',
  },
  {
    value: 'gender' as const,
    label: 'По полу',
    description: 'Массовая отправка мужчинам или женщинам',
    icon: 'i-heroicons-users',
  },
  {
    value: 'abandoned_cart' as const,
    label: 'Брошенные корзины',
    description: 'Пользователи с незавершенными заказами',
    icon: 'i-heroicons-shopping-cart',
  },
  {
    value: 'new_guests' as const,
    label: 'Новые гости',
    description: 'Недавно зарегистрированные пользователи',
    icon: 'i-heroicons-sparkles',
  },
]

// Шаблоны по типам уведомлений
const templatesByType: Record<NotificationType, NotificationTemplate[]> = {
  individual: [
    { id: 'ind1', title: 'Персональное предложение', body: 'Специально для вас - скидка 15%!', category: 'individual', variables: ['username'] },
    { id: 'ind2', title: 'С днем рождения!', body: 'Поздравляем с днем рождения! Подарок ждет вас', category: 'individual', variables: ['username', 'birth_date'] },
  ],
  gender: [
    { id: 'gen1', title: 'Акция для мужчин', body: 'Мужское меню со скидкой 20%', category: 'gender' },
    { id: 'gen2', title: 'Акция для женщин', body: 'Десерты и легкие салаты - 15% скидка', category: 'gender' },
  ],
  abandoned_cart: [
    { id: 'cart1', title: 'Ваш заказ ждет', body: 'Не забудьте завершить заказ - блюда еще горячие!', category: 'abandoned_cart' },
    { id: 'cart2', title: 'Скидка на незавершенный заказ', body: 'Завершите заказ со скидкой 10%', category: 'abandoned_cart' },
  ],
  new_guests: [
    { id: 'new1', title: 'Добро пожаловать!', body: 'Скидка 20% на первый заказ для новых гостей!', category: 'new_guests' },
    { id: 'new2', title: 'Изучите наше меню', body: 'Попробуйте наши хиты - рекомендуем начать с них', category: 'new_guests' },
  ],
}

const customForm = reactive({
  title: '',
  body: '',
  dataJson: '',
  scheduleDate: '',
  scheduleTime: '',
})

// Вычисляемые свойства
/**
 * Момент отправки в ISO. Пусто — отправить сразу.
 * Дата и время приходят из полей без зоны, поэтому new Date() трактует их как
 * местное время администратора — это и требуется.
 */
const scheduledAtIso = computed(() => {
  if (!isScheduled.value || !customForm.scheduleDate || !customForm.scheduleTime) return ''
  const parsed = new Date(`${customForm.scheduleDate}T${customForm.scheduleTime}`)
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString()
})

const currentTemplates = computed(() => templatesByType[selectedType.value] || [])

const canSend = computed(() => {
  if (selectedTemplate.value) return true
  if (!customForm.title || !customForm.body) return false

  // Дополнительные проверки по типу
  if (selectedType.value === 'individual' && !individualUserId.value) return false
  if (selectedType.value === 'gender' && !genderFilter.value) return false

  return true
})

const canSendTest = computed(() => {
  return canSend.value && selectedType.value !== 'individual'
})

const dataJsonError = computed(() => {
  if (!customForm.dataJson) return null
  try {
    JSON.parse(customForm.dataJson)
    return null
  } catch {
    return 'Некорректный JSON формат'
  }
})

// Методы
const loadAudienceStats = async () => {
  try {
    audienceStats.value = await APIAdminNotification.getAudienceStats()
  } catch (error) {
    consola.error('Ошибка загрузки статистики аудитории:', error)
  }
}

const selectNotificationType = (type: NotificationType) => {
  selectedType.value = type
  selectedTemplate.value = ''
  // Сбрасываем форму при смене типа
  Object.assign(customForm, {
    title: '',
    body: '',
    dataJson: '',
    scheduleDate: '',
    scheduleTime: '',
  })
}

const selectTemplate = (template: NotificationTemplate) => {
  selectedTemplate.value = template.id
  if (showCustomForm.value) {
    customForm.title = template.title
    customForm.body = template.body
  }
}

const getAudienceCount = (type: NotificationType): number => {
  if (!audienceStats.value) return 0

  switch (type) {
    case 'individual': { return 1 }
    case 'gender': { return audienceStats.value.male_users + audienceStats.value.female_users }
    case 'abandoned_cart': { return audienceStats.value.users_with_abandoned_carts }
    case 'new_guests': { return audienceStats.value.new_guests }
    default: { return 0 }
  }
}

const getTodayDate = () => {
  return new Date().toISOString().split('T')[0]
}

const buildPayload = () => {
  let data: Record<string, string> = {}

  if (customForm.dataJson) {
    try {
      data = JSON.parse(customForm.dataJson)
    } catch {
      consola.error('Invalid JSON in data field')
    }
  }

  const title = customForm.title || currentTemplates.value.find(t => t.id === selectedTemplate.value)?.title || ''
  const body = customForm.body || currentTemplates.value.find(t => t.id === selectedTemplate.value)?.body || ''

  // Общее для всех типов: признак служебного сообщения и момент отправки.
  // scheduled_at пустой = отправить сразу (сервер трактует так же).
  const base = {
    title,
    body,
    data,
    isService: isServiceMessage.value,
    ...(scheduledAtIso.value ? { scheduled_at: scheduledAtIso.value } : {}),
  }

  switch (selectedType.value) {
    case 'individual': {
      // Индивидуальное — не рассылка: согласия на рекламу не требует,
      // сервер такого поля у него и не ждёт.
      const { isService: _isService, ...rest } = base
      return { ...rest, userId: individualUserId.value! } as IndividualNotifyPayload
    }
    case 'gender': {
      return { ...base, gender: genderFilter.value } as GenderNotifyPayload
    }
    case 'abandoned_cart': {
      return base as AbandonedCartNotifyPayload
    }
    case 'new_guests': {
      return base as NewGuestsNotifyPayload
    }
    default: {
      throw new Error('Unknown notification type')
    }
  }
}

const sendPush = async () => {
  if (!canSend.value) return

  loading.value = true
  try {
    const payload = buildPayload()

    // Отдельной ветки «запланировать» больше нет: момент отправки едет полем
    // scheduled_at в том же запросе, а очередь держит задачу до срока. Прежняя
    // ветка звала /admin/notifications/schedule — такого маршрута не существует.
    switch (selectedType.value) {
      case 'individual': {
        await APIAdminNotification.notifyUser(payload as IndividualNotifyPayload)
        break
      }
      case 'gender': {
        await APIAdminNotification.notifyByGender(payload as GenderNotifyPayload)
        break
      }
      case 'abandoned_cart': {
        await APIAdminNotification.notifyAbandonedCart(payload as AbandonedCartNotifyPayload)
        break
      }
      case 'new_guests': {
        await APIAdminNotification.notifyNewGuests(payload as NewGuestsNotifyPayload)
        break
      }
    }

    modalStore.close()
    // TODO: показать уведомление об успехе
  } catch (error) {
    consola.error('Ошибка отправки уведомления:', error)
    // TODO: показать уведомление об ошибке
  } finally {
    loading.value = false
  }
}

const sendTestPush = async () => {
  testLoading.value = true
  try {
    // Отправляем тестовое уведомление администратору
    const payload = buildPayload()

    // Предполагаем, что текущий пользователь - администратор
    const testPayload: IndividualNotifyPayload = {
      userId: 1, // ID текущего администратора
      title: `[ТЕСТ] ${payload.title}`,
      body: payload.body,
      data: payload.data,
    }

    await APIAdminNotification.notifyUser(testPayload)
    // TODO: показать уведомление об успешной отправке теста
  } catch (error) {
    consola.error('Ошибка отправки тестового уведомления:', error)
  } finally {
    testLoading.value = false
  }
}

// Загружаем статистику при монтировании
onMounted(() => {
  loadAudienceStats()
})

// Сбрасываем выбранный шаблон при смене типа
watch(selectedType, () => {
  selectedTemplate.value = ''
})
</script>
